import { formatAddress } from "@inrev/common";
import type { SummaryFieldProps } from "@portal/components/multiform";
import {
	SummaryCardSection,
	SummaryField,
	SummaryFieldGroup,
	SummarySection,
} from "@portal/components/multiform";
import { Separator } from "@portal/components/ui/Separator";
import { useIsCommercialSuretyEnabled } from "@portal/hooks/useIsCommercialSuretyEnabled";
import { Fragment, type ReactElement } from "react";
import { useFormContext } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { match } from "ts-pattern";
import { exposureSizeOptions } from "../../../../../../constants";
import { boolToYN, formatEmpty } from "../../../../../../utils";
import {
	type AccountFormValues,
	type CompanyFormData,
	fp,
	type IndividualFormData,
	type IndividualGroup,
	type OwnerFormData,
	type SpouseFormData,
} from "../../schema";

const companyDisplayOrder: (keyof CompanyFormData)[] = [
	"name",
	"fein",
	"address",
	"isPrimaryCompany",
];

const ownerDisplayOrder: (keyof OwnerFormData | "name")[] = [
	"name",
	"address",
	"email",
	"ssn",
	"ownsAHome",
	"creditReportPermission",
];

const companyFieldPath = (index: number, field: keyof CompanyFormData) =>
	`${fp("account.details.companies")}.${index}.${field}`;

const individualFieldPath = (groupIndex: number, memberIndex: number, field: string) =>
	`${fp("account.details.individuals")}.${groupIndex}.${memberIndex}.${field}`;

export const DetailsSummary = () => {
	const { getValues } = useFormContext<AccountFormValues>();
	const { pathname } = useLocation();
	const isCommercialSuretyEnabled = useIsCommercialSuretyEnabled();

	const values = getValues();
	const modelSize = values.account.modelSize;
	const companies = values.account.details.companies as CompanyFormData[];
	const groups = (values.account.details.individuals ?? []) as IndividualGroup[];

	const to = (id?: string) => ({ pathname, section: "details", id });

	const getCompanyField = (
		company: CompanyFormData,
		index: number,
		key: keyof CompanyFormData,
	): ReactElement<SummaryFieldProps> => {
		return match(key)
			.with("name", () => (
				<SummaryField
					label="Name"
					value={company.name}
					fieldPath={companyFieldPath(index, "name")}
					key={`${index}-${key}`}
					to={to("companies")}
				/>
			))
			.with("fein", () => (
				<SummaryField
					label="FEIN"
					value={company.fein}
					fieldPath={companyFieldPath(index, "fein")}
					key={`${index}-${key}`}
					to={to("companies")}
				/>
			))
			.with("address", () => (
				<SummaryField
					label="Address"
					value={company.address ? formatAddress(company.address) : undefined}
					fieldPath={companyFieldPath(index, "address")}
					key={`${index}-${key}`}
					to={to("companies")}
				/>
			))
			.with("isPrimaryCompany", () => <></>)
			.exhaustive();
	};

	const formatIndividualName = (ind: IndividualFormData) =>
		[ind.firstName, ind.middleInitial, ind.lastName, ind.suffix].filter(Boolean).join(" ") ||
		undefined;

	const getOwnerField = (
		owner: OwnerFormData,
		groupIndex: number,
		key: keyof OwnerFormData | "name",
	): ReactElement<SummaryFieldProps> => {
		return match(key)
			.with("name", () => (
				<SummaryField
					label="Name"
					value={formatIndividualName(owner)}
					fieldPath={individualFieldPath(groupIndex, 0, "firstName")}
					key={`${groupIndex}-${key}`}
					to={to("individuals")}
				/>
			))
			.with("address", () => (
				<SummaryField
					label="Address"
					value={owner.address ? formatAddress(owner.address) : undefined}
					fieldPath={individualFieldPath(groupIndex, 0, "address")}
					key={`${groupIndex}-${key}`}
					to={to("individuals")}
				/>
			))
			.with("email", () => (
				<SummaryField
					label="Email"
					value={owner.email}
					fieldPath={individualFieldPath(groupIndex, 0, "email")}
					key={`${groupIndex}-${key}`}
					to={to("individuals")}
				/>
			))
			.with("ssn", () => (
				<SummaryField
					label="SSN"
					value={owner.ssn}
					fieldPath={individualFieldPath(groupIndex, 0, "ssn")}
					key={`${groupIndex}-${key}`}
					to={to("individuals")}
				/>
			))
			.with("ownsAHome", () => (
				<SummaryField
					label="Owns A Home"
					value={boolToYN(owner.ownsAHome)}
					fieldPath={individualFieldPath(groupIndex, 0, "ownsAHome")}
					key={`${groupIndex}-${key}`}
					to={to("individuals")}
				/>
			))
			.with("creditReportPermission", () => (
				<SummaryField
					label="Credit Report Authorization"
					value={boolToYN(owner.creditReportPermission)}
					fieldPath={individualFieldPath(groupIndex, 0, "creditReportPermission")}
					key={`${groupIndex}-${key}`}
					to={to("individuals")}
				/>
			))
			.otherwise(() => <></>);
	};

	const getOwnerItems = (
		owner: OwnerFormData,
		groupIndex: number,
		spouse: (OwnerFormData | SpouseFormData) | undefined,
	) => {
		const spouseIsAlsoOwner = spouse?.type === "owner";
		const showSpouseDetails = !!spouse && !spouseIsAlsoOwner;
		const items: ReactElement<SummaryFieldProps>[] = [
			...ownerDisplayOrder.map((key) => getOwnerField(owner, groupIndex, key)),
			<SummaryField
				key={`${groupIndex}-married`}
				label="Married"
				value={boolToYN(!!spouse)}
				to={to("individuals")}
			/>,
			...(!!spouse && spouseIsAlsoOwner
				? [
						<SummaryField
							key={`${groupIndex}-spouse`}
							label="Spouse"
							value={formatEmpty(formatIndividualName(spouse))}
							to={to("individuals")}
						/>,
					]
				: []),
			...(showSpouseDetails
				? [
						<SummaryField
							key={`${groupIndex}-spouse-name`}
							label="Spouse Name"
							value={formatEmpty(formatIndividualName(spouse))}
							to={to("individuals")}
						/>,
						<SummaryField
							key={`${groupIndex}-spouse-address`}
							label="Spouse Address"
							value={spouse.address ? formatAddress(spouse.address) : formatEmpty(undefined)}
							to={to("individuals")}
						/>,
						<SummaryField
							key={`${groupIndex}-spouse-email`}
							label="Spouse Email"
							value={formatEmpty(spouse.email)}
							to={to("individuals")}
						/>,
					]
				: []),
		];
		return items;
	};

	const exposureSizeLabel = exposureSizeOptions.find((o) => o.value === modelSize)?.label;

	return (
		<SummaryCardSection header="Principal" to={{ pathname, section: "details" }}>
			<SummaryFieldGroup>
				{isCommercialSuretyEnabled && (
					<SummaryField
						label="Needs Payment & Performance Bonds"
						value={boolToYN(getValues("requiresPaymentAndPerformanceBonds"))}
						fieldPath={fp("requiresPaymentAndPerformanceBonds")}
						to={to("requiresPaymentAndPerformanceBonds")}
					/>
				)}
				<SummaryField
					label="12 Month Bonding Need"
					value={exposureSizeLabel}
					fieldPath={fp("account.modelSize")}
					to={to("modelSize")}
				/>
			</SummaryFieldGroup>
			<SummarySection
				header="Companies"
				fieldPath={fp("account.details.companies")}
				to={{ pathname, section: "details", id: "companies" }}
			>
				{companies.length === 0 && <div className="text-[14px] font-normal text-primary">None</div>}
				{companies.map((company: CompanyFormData, index: number) => (
					<Fragment key={`${index}-company`}>
						<SummaryFieldGroup>
							{companyDisplayOrder.map((key) => getCompanyField(company, index, key))}
						</SummaryFieldGroup>
						{index !== companies.length - 1 && (
							<Separator orientation="horizontal" className="bg-bc-default" />
						)}
					</Fragment>
				))}
			</SummarySection>
			<SummarySection
				header="Individuals"
				fieldPath={fp("account.details.individuals")}
				to={{ pathname, section: "details", id: "individuals" }}
			>
				{groups.length === 0 && <div className="text-[14px] font-normal text-primary">None</div>}
				{groups.map((group, groupIndex) => {
					const owner =
						group.find((ind): ind is OwnerFormData => ind.type === "owner") ??
						(group[0] as OwnerFormData);
					const spouse = group.find((ind) => ind !== owner) as IndividualFormData | undefined;
					const spouseIsAlsoOwner = spouse?.type === "owner";
					return (
						<Fragment key={`individual-summary-${groupIndex}`}>
							<SummaryFieldGroup>{getOwnerItems(owner, groupIndex, spouse)}</SummaryFieldGroup>
							{spouseIsAlsoOwner && (
								<>
									<Separator orientation="horizontal" className="bg-bc-default" />
									<SummaryFieldGroup>
										{getOwnerItems(spouse as OwnerFormData, groupIndex, owner)}
									</SummaryFieldGroup>
								</>
							)}
							{groupIndex !== groups.length - 1 && (
								<Separator orientation="horizontal" className="bg-bc-default" />
							)}
						</Fragment>
					);
				})}
			</SummarySection>
		</SummaryCardSection>
	);
};
